// React
import { gql, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

// Components
import { Card, Grid, Icon, MenuItem, Select } from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Data
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import dataPermisos from "./data/dataPermisos";
import ModalPermiso from "./ModalPermiso";
import { GiDeer } from "react-icons/gi";

function PermisosCaza() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedPermiso, setSelectedPermiso] = useState(null);
  const [openModalPermiso, setOpenModalPermiso] = useState(false);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "ASC",
  });
  const { columns, getRows } = dataPermisos;
  const [filter, setFilter] = useState({
    estado: "En revisión",
  });
  const [campoPendiente, setCampoPendiente] = useState(false);
  const [acompaniantePendiente, setAcompaniantePendiente] = useState(false);

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const { loading, error, data, refetch } = useQuery(
    gql`
      query getPeremisos(
        $search: String
        $page: Int
        $order: Order
        $filter: JSON
        $campoPendiente: Boolean
        $acompaniantePendiente: Boolean
      ) {
        paginationInfo {
          pages
          total
        }
        permisosGob(
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
          filter: $filter
          campoPendiente: $campoPendiente
          acompaniantePendiente: $acompaniantePendiente
        ) {
          id
          tipo
          estado
          medioDePago
          usuario {
            nombre
            apellido
            username
            dni
            email
          }
          fechaVencimiento
          anioPermiso
          nroPermiso
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: {
          ...filter,
          tipo: [
            "Permiso de caza mayor",
            "Permiso de caza menor",
            "Permiso de caza mayor y menor",
            "Permiso de acompañante de caza",
          ],
        },
        campoPendiente: campoPendiente,
        acompaniantePendiente: acompaniantePendiente,
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (data) {
      setRows(
        getRows(data.permisosGob, (item) => {
          setSelectedPermiso(item);
          setOpenModalPermiso(true);
        })
      );
      setPagesInfo(data.paginationInfo);
    }
  }, [data]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexDirection={{
                    xs: "column",
                    md: "row",
                  }}
                >
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftBox display="flex" alignItems="center">
                      <SoftBox mr={1} display="flex" alignItems="center">
                        <SoftBox
                          display="grid"
                          justifyContent="center"
                          alignItems="center"
                          bgColor="primary"
                          color="white"
                          width="3rem"
                          height="3rem"
                          shadow="md"
                          borderRadius="lg"
                          variant="gradient"
                        >
                          <GiDeer size={24} />
                        </SoftBox>
                      </SoftBox>
                      <SoftBox
                        display="flex-row"
                        alignItems="center"
                        sx={{
                          lineHeight: 0,
                        }}
                      >
                        <SoftTypography variant="h6" fontWeight="bold">
                          Permisos de Caza
                        </SoftTypography>
                        <SoftTypography variant="caption" fontWeight="regular">
                          {pagesInfo?.total
                            ? `${pagesInfo?.total} permiso${pagesInfo?.total > 1 ? "s" : ""}`
                            : "No hay permisos"}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    flexDirection={{
                      xs: "column",
                      md: "row",
                    }}
                    alignItems="center"
                    justifyContent="end"
                    mt={{
                      xs: 2,
                      md: 0,
                    }}
                    gap={2}
                    sx={{
                      width: {
                        xs: "100%",
                        md: "auto",
                      },
                    }}
                  >
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          md: "auto",
                        },
                      }}
                    >
                      <Select
                        value={filter.estado || -1}
                        onChange={(e) => {
                          setFilter({
                            ...filter,
                            estado: e.target.value === -1 ? undefined : e.target.value,
                          });
                          setPage(1);
                        }}
                      >
                        <MenuItem value={-1}>Todos los estados</MenuItem>
                        <MenuItem value={"Pendiente de pago"}>Pendiente de pago</MenuItem>
                        <MenuItem value={"En revisión"}>En revisión</MenuItem>
                        <MenuItem value={"Aprobado"}>Aprobado</MenuItem>
                        <MenuItem value={"Rechazado"}>Rechazado</MenuItem>
                      </Select>
                    </SoftBox>
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          md: "auto",
                        },
                      }}
                    >
                      <Select
                        value={campoPendiente || false}
                        onChange={(e) => {
                          setCampoPendiente(e.target.value);
                          setPage(1);
                        }}
                      >
                        <MenuItem value={false}>Todos los estados campo</MenuItem>
                        <MenuItem value={true}>Campo pendiente</MenuItem>
                      </Select>
                    </SoftBox>
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          md: "auto",
                        },
                      }}
                    >
                      <Select
                        value={acompaniantePendiente || false}
                        onChange={(e) => {
                          setAcompaniantePendiente(e.target.value);
                          setPage(1);
                        }}
                      >
                        <MenuItem value={false}>Todos los estados acompañante</MenuItem>
                        <MenuItem value={true}>Acompañante pendiente</MenuItem>
                      </Select>
                    </SoftBox>
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          md: "auto",
                        },
                      }}
                    >
                      <SoftInput
                        placeholder="Escribe aquí..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                      />
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Loading />
              ) : rows?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={data?.permisosGob.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          <ModalPermiso
            open={openModalPermiso}
            handleClose={() => setOpenModalPermiso(false)}
            idPermiso={selectedPermiso?.id}
            refetch={refetch}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PermisosCaza;
